import React from 'react';
import { Redirect, Route, RouteProps, Switch } from 'react-router-dom';
import { ConnectedRouter } from 'connected-react-router';
import { connect } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import { ThunkDispatch } from 'redux-thunk';
import { hotjar } from 'react-hotjar';
import { GA4React } from 'ga-4-react';
import setupModule from './modules/setup/setup.index';
import adminModule from './modules/admin/admin.index';
import ratesModule from './modules/rates/rates.index';
import reportsModule from './modules/reports/reports.index';
import reservationModule from './modules/reservations/reservation.index';
import Header from './components/Header';
import { Navigation } from './types/app.header';
import {
  AppProps,
  LOCALSTORAGE_ACTIVE_SUPPLIER,
  STORED_ACRISS_CODES,
  STORED_DESK_GROUP_ID,
  STORED_PRODUCT_ID
} from './types/app.index';
import Login from './modules/authentication/views/Login';
import { AppState } from './store/store.types';
import ErrorPage from './modules/errorhandling/views/ErrorPage';
import { reservationViewRoles } from './api/reservation/reservation.types';
import { dashboardViewRole, UserRole } from './api/authentication/authentication.types';
import { AuthenticationActionTypes } from './modules/authentication/types/authentication.types';
import { storeActiveSupplier } from './modules/authentication/actions/authentication.action';
import { saveAcrissCodes, storeDeskGroupId, storeProductId } from './modules/rates/actions/rateplan.actions';
import ReservationDetails from './modules/reservations/view/ReservationDetails';
import { setupModuleViewRoles } from './api/core/core.types';
import ForgotPassword from './modules/authentication/views/ForgotPassword';
import ResetPassword from './modules/authentication/views/ResetPassword';
import { withTranslation } from 'react-i18next';
import { adminModuleViewRoles, SupplierModel } from './api/admin/admin.types';
import DashboardEntity from './modules/dashboard/dashboard.index';

const navigation: Navigation = {
  links: [
    {
      title: 'm1.dashboard_Dashboard menu',
      route: '/dashboard',
      roles: dashboardViewRole
    },
    {
      title: 'm1.reservations_Reservations menu',
      route: '/reservations',
      roles: reservationViewRoles
    },
    {
      title: 'm1.rates',
      route: '/rates',
      subnav: ratesModule.subnav
    },
    {
      title: 'm1.setup',
      route: '/setup',
      subnav: setupModule.subnav,
      roles: setupModuleViewRoles
    },
    {
      title: 'Reports',
      route: '/reports',
      roles: dashboardViewRole,
      subnav: reportsModule.subnav
    },
    {
      title: 'Admin',
      route: '/admin',
      roles: adminModuleViewRoles,
      subnav: adminModule.subnav
    }
  ]
};

const mapStateToRouteProps = (state: AppState) => ({
  token: state.authentication.token
});

const ProtectedRoute = connect(mapStateToRouteProps, null)(
  ({ token, ...props }: { token?: string } & RouteProps) => {
    if (!token) {
      return <Route {...props} render={() => <Redirect to="/login"/>}/>;
    }
    return <Route {...props} />;
  }
);

class App extends React.PureComponent<AppProps, AppState> {

  async componentDidMount() {
    const storedActiveSupplier = localStorage.getItem(LOCALSTORAGE_ACTIVE_SUPPLIER);
    if (storedActiveSupplier) {
      const activeSupplier = JSON.parse(storedActiveSupplier) as SupplierModel;

      if (activeSupplier) {
        this.props.storeActiveSupplier(activeSupplier);
      }
    }

    const storedProductId = localStorage.getItem(STORED_PRODUCT_ID);
    if (storedProductId) {
      const productId = JSON.parse(storedProductId) as number;

      if (productId) {
        this.props.storeProductId(productId);
      }
    }

    const storedDeskGroupId = localStorage.getItem(STORED_DESK_GROUP_ID);
    if (storedDeskGroupId) {
      const deskGroupId = JSON.parse(storedDeskGroupId) as number;

      if (deskGroupId) {
        this.props.storeDeskGroupId(deskGroupId);
      }
    }

    const storedAcrissCodes = localStorage.getItem(STORED_ACRISS_CODES);
    if (storedAcrissCodes) {
      const acrissCodes = JSON.parse(storedAcrissCodes) as string[];
      if (acrissCodes) {
        this.props.storeAcrissCodes(acrissCodes);
      }
    }

    const ga4react = new GA4React(window.config.gaMeasurementId);
    await ga4react.initialize();

    this.props.history.listen((location: any, action: any) => {
      ga4react.pageview(location.pathname);
    });

    hotjar.initialize(Number(window.config.hotJarSiteId), Number(window.config.hotJarSiteVersion));
  }

  getRatesRedirect = () => {
    if (this.props.user) {
      if (this.props.user.roles.find((r) => r === UserRole.BROKER) || this.props.user.roles.find((r) => r === UserRole.BOOKING_MANAGER)) {
        return '/rates/stopsells';
      }
    }

    return '/rates/rateplans';
  };

  render() {
    const { tReady } = this.props;

    if (!tReady) return <div></div>; // Loading translations
    const { t } = this.props;

    return (
      <ConnectedRouter history={this.props.history}>
        <Switch>
          <Redirect exact from="/" to="/reservations"/>
          <Redirect exact from="/setup" to="/setup/carmodels"/>
          <Redirect exact from="/rates" to={this.getRatesRedirect()}/>
          <Redirect exact from="/reports" to="/reports/insights"/>
          <Redirect exact from="/admin" to="/admin/configuration"/>
          <Route
            exact
            path="/login"
            render={(routeProps) => <Login {...routeProps} />}
          />
          <Route
            exact
            path="/forgotpassword"
            render={(routeProps) => <ForgotPassword {...routeProps} />}
          />
          <Route
            exact
            path="/changepassword/:changePasswordId"
            render={(routeProps) => <ResetPassword {...routeProps} />}
          />
          <Route
            exact
            path="/:supplier/reservation/:number/:uuid"
            render={(routeProps) => (
              <ReservationDetails {...routeProps}>
                <Header {...routeProps} navigation={navigation}/>
              </ReservationDetails>
            )}
          />
          <Route
            exact
            path="/404"
            render={(routeProps) => (
              <ErrorPage {...routeProps} code="404" errorMessage={t('text.error_page.object_not_found')}>
                <Header {...routeProps} navigation={navigation}/>
              </ErrorPage>
            )}
          />
          <Route
            exact
            path="/invalidpermissions"
            render={(routeProps) => (
              <ErrorPage {...routeProps} code="403" errorMessage={t('text.error_page.invalid_permissions')}>
                <Header {...routeProps} navigation={navigation}/>
              </ErrorPage>
            )}
          />
          <ProtectedRoute
            exact
            path="/dashboard"
            render={(routeProps: any) => (
              <DashboardEntity {...routeProps}>
                <Header {...routeProps} navigation={navigation}/>
              </DashboardEntity>
            )}
          />
          <ProtectedRoute
            path="/reservations"
            render={(routeProps: any) => (
              <reservationModule.reservationEntry {...routeProps}>
                <Header {...routeProps} navigation={navigation}/>
              </reservationModule.reservationEntry>
            )}
          />
          <ProtectedRoute
            path="/rates"
            render={(routeProps: any) => (
              <ratesModule.ratesEntry {...routeProps}>
                <Header {...routeProps} navigation={navigation}/>
              </ratesModule.ratesEntry>
            )}
          />
          <ProtectedRoute
            path="/setup"
            render={(routeProps: any) => (
              <setupModule.setupEntity {...routeProps}>
                <Header {...routeProps} navigation={navigation}/>
              </setupModule.setupEntity>
            )}
          />
          <ProtectedRoute
            path="/reports"
            render={(routeProps: any) => (
              <reportsModule.reportsEntry {...routeProps}>
                <Header {...routeProps} navigation={navigation}/>
              </reportsModule.reportsEntry>
            )}
          />
          <ProtectedRoute
            path="/admin"
            render={(routeProps: any) => (
              <adminModule.adminEntity {...routeProps}>
                <Header {...routeProps} navigation={navigation}/>
              </adminModule.adminEntity>
            )}
          />
        </Switch>
        <ToastContainer/>
      </ConnectedRouter>
    );
  }
}

const mapStateToProps = (state: AppState) => ({
  user: state.authentication.user,
  suppliers: state.authentication.suppliers
});

const mapDispatchToProps = (dispatch: ThunkDispatch<{}, {}, AuthenticationActionTypes>) => ({
  storeActiveSupplier: (supplier: SupplierModel) => dispatch(storeActiveSupplier(supplier)),
  storeProductId: (id: number) => dispatch(storeProductId(id)),
  storeDeskGroupId: (id: number) => dispatch(storeDeskGroupId(id)),
  storeAcrissCodes: (acrissCodes: string[]) => dispatch(saveAcrissCodes(acrissCodes))
});

export default withTranslation()(connect(
  mapStateToProps,
  mapDispatchToProps
)(App));
